import { useNavigate } from 'react-router-dom';
import { Plus, Car, Bell, Mail, ClipboardList, Wrench } from 'lucide-react';
import type { CustomerVehicle } from './types';
import { VehicleMotPanel } from '../components/VehicleMotPanel';

interface VehiclesTabProps {
    customerId: string;
    vehicles: CustomerVehicle[];
    vehiclesWithHistory: Set<string>;
    onAddVehicle: () => void;
    onMotUpdate: (vehicleId: string, patch: { mot_expiry?: string | null; mot_reminder_enabled?: boolean }) => void;
    /** Re-reads the vehicle row after a DVSA refresh has rewritten its MOT columns. */
    onVehicleRefreshed: (vehicleId: string) => void;
    onOpenServiceHistory: (vehicleId?: string) => void;
    onOpenEmail: (templateId?: string, vehicleId?: string) => void;
}

export function VehiclesTab({
    customerId,
    vehicles,
    vehiclesWithHistory,
    onAddVehicle,
    onMotUpdate,
    onVehicleRefreshed,
    onOpenServiceHistory,
    onOpenEmail,
}: VehiclesTabProps) {
    const navigate = useNavigate();

    return (
        <section className="bg-panel border border-line rounded-xl overflow-hidden">
            <div className="p-4 border-b border-line flex items-center justify-between bg-raised">
                <h3 className="font-bold text-fg flex items-center gap-2">
                    <Car className="w-4 h-4 text-accent" />
                    Vehicles
                </h3>
                <button
                    onClick={onAddVehicle}
                    className="text-xs fd-btn-primary px-3 py-1.5 rounded-md font-medium transition-opacity flex items-center gap-1"
                >
                    <Plus className="w-3.5 h-3.5" />
                    Add Vehicle
                </button>
            </div>

            <div className="divide-y divide-tint/5">
                {vehicles.length === 0 ? (
                    <div className="p-8 text-center text-fg-4 text-sm">
                        No vehicles linked to this customer yet.
                    </div>
                ) : (
                    vehicles.map(vehicle => (
                        <div key={vehicle.id} className="p-4 hover:bg-tint/5 transition-colors group">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex items-center gap-4 min-w-0">
                                <div className="w-12 h-12 rounded-lg bg-tint/5 flex items-center justify-center text-fg-4 group-hover:text-accent group-hover:bg-accent/10 transition-colors">
                                    <Car className="w-6 h-6" />
                                </div>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-mono bg-raised text-fg-2 text-xs px-1.5 py-0.5 rounded border border-tint/10">
                                            {vehicle.vrm}
                                        </span>
                                        <h4 className="font-bold text-fg">{vehicle.year} {vehicle.make} {vehicle.model}</h4>
                                    </div>
                                    <p className="text-xs text-fg-4 mt-1">VIN: {vehicle.vin || 'N/A'}</p>
                                    {/* MOT due date + reminder toggle */}
                                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                                        <span className="text-fd-sm-tight font-medium text-fg-4 uppercase tracking-wider">MOT due</span>
                                        <input
                                            type="date"
                                            value={vehicle.mot_expiry ? String(vehicle.mot_expiry).slice(0, 10) : ''}
                                            onChange={(e) => onMotUpdate(vehicle.id, { mot_expiry: e.target.value || null })}
                                            onClick={(e) => e.stopPropagation()}
                                            className="bg-tint/5 border border-tint/10 rounded px-2 py-1 text-xs text-fg focus:ring-1 focus:ring-accent outline-none [color-scheme:dark]"
                                        />
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); onMotUpdate(vehicle.id, { mot_reminder_enabled: !(vehicle.mot_reminder_enabled !== false) }); }}
                                            title="Toggle automatic MOT reminder emails for this vehicle"
                                            className={`inline-flex items-center gap-1.5 text-fd-sm-tight font-bold px-2 py-1 rounded border transition-colors ${
                                                vehicle.mot_reminder_enabled !== false
                                                    ? 'bg-success/10 text-success border-success/30'
                                                    : 'bg-tint/5 text-fg-4 border-tint/10'
                                            }`}
                                        >
                                            <Bell className="w-3 h-3" />
                                            Reminders {vehicle.mot_reminder_enabled !== false ? 'On' : 'Off'}
                                        </button>
                                        {vehicle.mot_expiry && (
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); onOpenEmail('mot_reminder', vehicle.id); }}
                                                title="Email this customer an MOT reminder"
                                                className="inline-flex items-center gap-1.5 text-fd-sm-tight font-bold px-2 py-1 rounded border bg-accent/10 text-accent border-accent/30 hover:bg-accent/20 transition-colors"
                                            >
                                                <Mail className="w-3 h-3" />
                                                Send reminder
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {/* Hover-reveal only where a pointer exists. On a
                                phone these two buttons are the whole point of
                                the row, and hiding them behind :hover made them
                                unreachable. */}
                            <div className="flex items-center gap-2 shrink-0 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                                {vehiclesWithHistory.has(vehicle.id) && (
                                    <button
                                        onClick={() => onOpenServiceHistory(vehicle.id)}
                                        className="text-xs bg-tint/5 hover:bg-tint/10 text-fg-2 px-3 py-1.5 rounded flex items-center gap-1.5 border border-tint/10"
                                        title="Service history for this vehicle"
                                    >
                                        <ClipboardList className="w-3.5 h-3.5" />
                                        History
                                    </button>
                                )}
                                <button
                                    onClick={() => navigate(`/workshop/new?vehicle_id=${vehicle.id}&customer_id=${customerId}`)}
                                    className="text-xs bg-tint/10 hover:bg-tint/20 text-fg px-3 py-1.5 rounded flex items-center gap-1.5"
                                >
                                    <Wrench className="w-3.5 h-3.5" />
                                    New Job
                                </button>
                            </div>
                          </div>

                          {/* The MOT record sits under the vehicle, full width,
                              because this is the screen where a mechanic decides
                              what to quote for — and last year's advisories are
                              the most useful thing on it. */}
                          <VehicleMotPanel
                              vehicle={vehicle}
                              onRefreshed={() => onVehicleRefreshed(vehicle.id)}
                          />
                        </div>
                    ))
                )}
            </div>
        </section>
    );
}
